import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { from, map, mergeMap, Observable, tap } from 'rxjs';
import { PageApiService } from './page-api-client.service';
import { PageImageLoadingPrioritizer } from './page-image-loading-prioritizer';
import { CtBatch } from '../model/ct-batch-model';

export const MAX_CONCURRENT_REQUESTS = 6;

interface ImageData {
  thumbnailSrc: WritableSignal<string | undefined>;
  src: WritableSignal<string | undefined>;
  isLoaded: Signal<boolean>;
  isThumbnailLoaded: Signal<boolean>;
  aspectRatio: WritableSignal<number>;
}

@Injectable({
  providedIn: 'root',
})
export class PageImageService {
  private readonly imageDataByPageId = new Map<string, ImageData>();

  readonly defaultImage = '@shared/logos/empty-page.png';

  private readonly pageApiClient = inject(PageApiService);
  private readonly pageImageLoadingPrioritizer = inject(PageImageLoadingPrioritizer);

  private readonly _loadingProgress = signal(0);
  readonly loadingProgress = this._loadingProgress.asReadonly();

  readonly thumbnailsInitialized = signal(false);

  preloadThumbnails(...batches: CtBatch[]) {
    if (this.thumbnailsInitialized()) return;

    this.initImageData(
      batches
        .flatMap((batch) => batch.documents)
        .flatMap((document) => document.pages.map((page) => page.id)),
    );
    batches.forEach((batch) => this.loadThumbnails(batch.id));
    this.thumbnailsInitialized.set(true);
  }

  init(pageIds: string[]) {
    this.pageImageLoadingPrioritizer.init(pageIds);

    this.initImageData(pageIds);
  }

  loadAroundPage(pageId: string) {
    const { pageIdsToLoad, pageIdsToUnload } =
      this.pageImageLoadingPrioritizer.requestLoadAroundPage(pageId);
    this.loadImageBlobs(pageIdsToLoad);
    for (const pageIdToUnload of pageIdsToUnload) {
      const url = this.imageDataByPageId.get(pageIdToUnload)?.src();
      if (url) {
        URL.revokeObjectURL(url);
        this.imageDataByPageId.get(pageIdToUnload)?.src.set(this.defaultImage);
      }
    }
  }

  initializeImageData(pageId: string) {
    const src = signal(this.defaultImage);
    const thumbnailSrc = signal(this.defaultImage);
    this.imageDataByPageId.set(pageId, {
      src,
      thumbnailSrc: thumbnailSrc,
      aspectRatio: signal(0.7 / 1),
      isThumbnailLoaded: computed(() => thumbnailSrc() !== this.defaultImage),
      isLoaded: computed(() => src() !== this.defaultImage),
    });
  }

  private loadImageBlobs(pageIds: Iterable<string>) {
    from(pageIds)
      .pipe(mergeMap((id) => this.loadImageBlob(id), MAX_CONCURRENT_REQUESTS))
      .subscribe();
  }

  loadImageBlob(pageId: string): Observable<string | undefined> {
    return this.pageApiClient.getImageForPage(pageId).pipe(
      map((imageBlob) => URL.createObjectURL(imageBlob)),
      tap((objectUrl) => this.imageDataByPageId.get(pageId)!.src.set(objectUrl)),
    );
  }

  getImageSrc(pageId: string): Signal<string | undefined> {
    return this.getImageData(pageId).src.asReadonly();
  }

  getThumbnailImageSrc(id: string): Signal<string | undefined> {
    return this.getImageData(id).thumbnailSrc.asReadonly();
  }

  /**
   * The difference between this function and getImageSrc is that the latter only checks
   * present image data (which was loaded given the current batch id) and throws an error otherwise.
   * If page data from other batches is required, this function can be used instead.
   */
  requestImageSrc(pageId: string): Signal<string | undefined> {
    if (!this.existsImageDataFor(pageId)) {
      this.initializeImageData(pageId);
      this.loadImageBlob(pageId).subscribe();
    }

    return this.getImageSrc(pageId);
  }

  isImageLoaded(pageId: string): Signal<boolean> {
    return this.getImageData(pageId).isLoaded;
  }

  isThumbnailImageLoaded(pageId: string): Signal<boolean> {
    return this.getImageData(pageId).isThumbnailLoaded;
  }

  getImageAspectRatio(pageId: string): Signal<number> {
    return this.getImageData(pageId).aspectRatio.asReadonly();
  }

  /**
   * Updates the aspect ratio of an image. There are basically two options:
   *
   * * Either we store this information in the database and transmit it together with the images
   * * Or we use the <img/> element's naturalWidth and naturalHeight to calculate this value
   *
   * Right now we use the latter approach.
   */
  updateImageAspectRatio(pageId: string, aspectRatio: number) {
    this.getImageData(pageId).aspectRatio.set(aspectRatio);
  }

  existsImageDataFor(pageId: string): boolean {
    return this.imageDataByPageId.has(pageId);
  }

  getImageData(pageId: string): ImageData {
    if (!this.imageDataByPageId.has(pageId)) {
      throw new Error(`image data requested for id '${pageId}' which does not exist in data map`);
    }

    return this.imageDataByPageId.get(pageId)!;
  }

  private loadThumbnails(batchId: string) {
    this.pageApiClient
      .loadThumbnails(batchId)
      .pipe(
        tap((result) => {
          const entries = Object.entries(result);
          entries.forEach(([pageId, thumbnailBase64], index) => {
            this._loadingProgress.set((index + 1) / entries.length);
            this.getImageData(pageId).thumbnailSrc.set(`data:image/jpeg;base64,${thumbnailBase64}`);
          });
        }),
      )
      .subscribe();
  }

  initImageData(pageIds: string[]) {
    pageIds
      .filter((id) => !this.imageDataByPageId.has(id))
      .forEach((pageId) => this.initializeImageData(pageId));
  }
}
